import React from 'react';
import { FC210, FC211 } from '../../../services/c212Api';
import { dateToInput, inputToDate } from '../helpers';

interface Tab1Props {
  formData: Partial<FC210>;
  names: Record<string, string>;
  details: Partial<FC211>[];
  selectedDetailRow: number;
  isEditing: boolean;
  getField: (key: keyof FC210) => FC210[keyof FC210];
  setField: (key: keyof FC210, value: FC210[keyof FC210]) => void;
  setDetails: React.Dispatch<React.SetStateAction<Partial<FC211>[]>>;
  setSelectedDetailRow: (idx: number) => void;
  addDetailRow: () => void;
  deleteDetailRow: () => void;
  resolveSingleName: (nameKey: string, type: string, code: string) => void;
}

/** Tab1: Page1 胚布預估 — Grid + master fields */
const Tab1ForecastDetail: React.FC<Tab1Props> = React.memo(({
  formData, names, details, selectedDetailRow, isEditing,
  getField, setField, setDetails, setSelectedDetailRow,
  addDetailRow, deleteDetailRow, resolveSingleName,
}) => {
  const handleDetailChange = (idx: number, key: keyof FC211, value: FC211[keyof FC211]) => {
    setDetails(prev => prev.map((d, i) => i === idx ? { ...d, [key]: value } : d));
  };

  return (
    <div className="tab-content-section">
      {/* Master fields (below grid in spec, placed first for clarity) */}
      <div className="form-grid" style={{ marginBottom: 16 }}>
        <label className="required">下單客戶</label>
        <div className="lookup-field">
          <input type="text" value={String(getField('fc210_12') || '')} style={{ width: 110 }}
            onChange={e => setField('fc210_12', e.target.value)}
            onBlur={() => resolveSingleName('customer', 'customer', String(getField('fc210_12') || ''))}
            disabled={!isEditing} />
          <span className="lookup-name">{names.customer || ''}</span>
        </div>

        <label className="required">成衣買主</label>
        <div className="lookup-field">
          <input type="text" value={String(getField('fc210_13') || '')} style={{ width: 110 }}
            onChange={e => setField('fc210_13', e.target.value)}
            onBlur={() => resolveSingleName('buyer', 'customer', String(getField('fc210_13') || ''))}
            disabled={!isEditing} />
          <span className="lookup-name">{names.buyer || ''}</span>
        </div>

        <label className="required">業務人員</label>
        <div className="lookup-field">
          <input type="text" value={String(getField('fc210_14') || '')} style={{ width: 68 }} maxLength={8}
            onChange={e => setField('fc210_14', e.target.value)}
            onBlur={() => {
              const code = String(getField('fc210_14') || '');
              resolveSingleName('salesPerson', 'employee', code);
              resolveSingleName('department', 'department', String(getField('fc210_15') || ''));
            }}
            disabled={!isEditing} />
          <span className="lookup-name">{names.salesPerson || ''}</span>
        </div>

        <label>部門</label>
        <span className="lookup-display">{names.department || String(getField('fc210_15') || '')}</span>

        <label>檢驗方式</label>
        <div className="lookup-field">
          <input type="text" value={String(getField('fc210_20') || '')} style={{ width: 80 }}
            onChange={e => setField('fc210_20', e.target.value)}
            onBlur={() => resolveSingleName('usage', 'usage', String(getField('fc210_20') || ''))}
            disabled={!isEditing} />
          <span className="lookup-name">{names.usage || ''}</span>
        </div>

        <label>產品用途</label>
        <div className="lookup-field">
          <input type="text" value={String(getField('fc210_21') || '')} style={{ width: 80 }}
            onChange={e => setField('fc210_21', e.target.value)}
            onBlur={() => resolveSingleName('buyerCode', 'garmentBuyer', String(getField('fc210_21') || ''))}
            disabled={!isEditing} />
          <span className="lookup-name">{names.buyerCode || ''}</span>
        </div>

        <label>打單人員</label>
        <div className="lookup-field">
          <input type="text" value={String(getField('fc210_18') || '')} style={{ width: 80 }}
            disabled />
          <span className="lookup-name">{names.createdBy || ''}</span>
        </div>

        <label>驗貨完成日</label>
        <input type="date" value={dateToInput(getField('fc210_19') as string | null)}
          onChange={e => setField('fc210_19', inputToDate(e.target.value) as FC210[keyof FC210])}
          disabled={!isEditing} />

        <label>總項次</label>
        <input type="number" value={formData.fc210_23 ?? 0} disabled style={{ width: 60 }} />
      </div>

      {/* Detail Grid (FC211) */}
      <div className="grid-header">
        <h3 className="section-title" style={{ marginBottom: 0 }}>預估明細</h3>
        <div className="grid-actions">
          <button className="btn btn-sm" disabled={!isEditing} onClick={addDetailRow}>明細新增</button>
          <button className="btn btn-sm btn-danger" disabled={!isEditing || details.length === 0} onClick={deleteDetailRow}>明細刪除</button>
        </div>
      </div>
      <div className="table-wrapper">
        <table className="data-table">
          <thead>
            <tr>
              <th style={{ width: 40 }}>S/N</th>
              <th style={{ width: 90 }}>產品代號1</th>
              <th style={{ width: 90 }}>產品代號2</th>
              <th style={{ width: 130 }}>預估數量</th>
              <th style={{ width: 120 }}>客戶需求日</th>
              <th style={{ width: 75 }}>單位</th>
            </tr>
          </thead>
          <tbody>
            {details.length === 0 && (
              <tr><td colSpan={6} style={{ textAlign: 'center', color: '#999' }}>無明細資料</td></tr>
            )}
            {details.map((d, i) => (
              <tr key={i}
                className={i === selectedDetailRow ? 'row-selected' : ''}
                onClick={() => setSelectedDetailRow(i)}>
                <td>{d.fc211_02}</td>
                <td>
                  <input type="text" value={d.fc211_10 || ''} disabled={!isEditing}
                    onChange={e => handleDetailChange(i, 'fc211_10', e.target.value)} />
                </td>
                <td>
                  <input type="text" value={d.fc211_11 || ''} disabled={!isEditing}
                    onChange={e => handleDetailChange(i, 'fc211_11', e.target.value)} />
                </td>
                <td>
                  <input type="number" value={d.fc211_12 ?? 0} disabled={!isEditing}
                    onChange={e => handleDetailChange(i, 'fc211_12', parseFloat(e.target.value) || 0)} />
                </td>
                <td>
                  <input type="date" value={dateToInput(d.fc211_13)} disabled={!isEditing}
                    onChange={e => handleDetailChange(i, 'fc211_13', inputToDate(e.target.value) as FC211[keyof FC211])} />
                </td>
                <td>
                  <input type="text" value={d.fc211_14 || ''} disabled={!isEditing}
                    onChange={e => handleDetailChange(i, 'fc211_14', e.target.value)} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
});

Tab1ForecastDetail.displayName = 'Tab1ForecastDetail';
export default Tab1ForecastDetail;
